import {
  OtysVacancyDetailPairs,
  VacanciesListParams,
  Vacancy,
  VacancyContent,
  VacancyDetail,
  VacancyDetailsCard,
} from '@/interfaces/vacancy'
import { Methods } from '@/interfaces/json-rpc'
import { ImageCategory, constructImageUrl } from '../url'
import { createSingleMatchCriteriaRes } from './shared'
import { openVacancyStatusId } from '../environment'
import { matchCriteriaFilterIds } from '../constants'
import { MathCriteriaReqParam } from '@/interfaces/match-criteria'

export type VacancyCallParams = {
  method: Methods.GET_VACANCY
  args: [string, string, typeof vacancyParams]
}

export const vacancyParams = {
  uid: true,
  title: true,
  published: true,
  entryDateTime: true,
  location: true,
  photoFileName: true,
  photoThumbFileName: true,
  relationId: true,
  salaryValue: true,
  status: true,
  statusId: true,
  userId: true,
  matchCriteria_4: true,
  matchCriteria_7: true,
  textField_companyProfile: true,
  textField_companyCulture: true,
  textField_description: true,
  textField_extra1: true,
  textField_extra2: true,
  textField_extra3: true,
  textField_extra4: true,
  textField_requirements: true,
  textField_salary: true,
  textField_summary: true,
  extraInfo8: true,
  jobType: true,
  locationAddress: true,
  locationCity: true,
  locationCountry: true,
  locationCountryCode: true,
  locationPostcode: true,
  locationState: true,
  relation: true,
  salaryUnit: true,
  publicationStartDate: true,
  publicationStatusId: true,
}

export const constructVacancyCallParams: (
  sid: string,
  vid: string
) => VacancyCallParams = (sid: string, vid: string) => {
  return {
    method: Methods.GET_VACANCY,
    args: [sid, vid, vacancyParams],
  }
}

export const createVacanciesResObj = (
  vacanciesList: Vacancy[] | undefined,
  sid: string
): VacancyDetailsCard[] | [] => {
  if (!vacanciesList) return []
  const responseObj = vacanciesList
    .filter((data) => data)
    .map((data) => {
      const hourlyRate = createSingleMatchCriteriaRes(data.matchCriteria_4)

      return {
        uid: data.uid || null,
        title: data.title || null,
        published: data.published || null,
        location: data.location || null,
        description: data.textField_summary?.text || null,
        image: data.photoFileName || null,
        photoFileName: constructImageUrl({
          ouid: data.photoFileName,
          sid,
          category: ImageCategory.VACANCY,
        }),
        hourlyRate,
        hoursPerWeek: data.extraInfo8 ?? '',
        salary: data.salaryValue || null,
        status: data.status || null,
        publicationStatusId: data.publicationStatusId || null,
      }
    })

  return responseObj
}

const constructQueryParams = ({
  searchQuery,
  filters,
}: {
  searchQuery: string
  filters: string[]
}) => {
  const searchQueryObj = {
    value: searchQuery,
    options: {
      enableExpansion: false,
      required: true,
      searchList: ['title', 'chapo', 'refs'],
      searchMode: 'one',
    },
  }

  const filterByMatchCriterium = (
    filters: string[],
    preFix: string
  ): string[] => {
    return filters.filter((item) => {
      const itemNumber = parseInt(item.split('_')[0])
      return itemNumber === parseInt(preFix)
    })
  }

  const filterObj = matchCriteriaFilterIds.reduce((result, el) => {
    const filterResults = filterByMatchCriterium(filters, el)
    if (filterResults.length > 0) {
      result[`C_${el}`] = {
        options: { required: true },
        value: {
          required: filterResults.map((el) => el.split('_')[1]),
        },
      }
    }
    return result
  }, {} as MathCriteriaReqParam)

  switch (true) {
    case filters && filters.length > 0 && !!searchQuery:
      return {
        ACTONOMY: {
          DATA: {
            keywords: { ...searchQueryObj },
            matchcriteria: { ...filterObj },
          },
          SUB_VERSION: 0,
          VERSION: 2,
        },
      }
    case filters && filters.length > 0 && !searchQuery:
      return {
        ACTONOMY: {
          DATA: {
            matchcriteria: { ...filterObj },
          },
          SUB_VERSION: 0,
          VERSION: 2,
        },
      }
    case !!searchQuery && filters.length === 0:
      return {
        ACTONOMY: {
          DATA: {
            keywords: { ...searchQueryObj },
          },
          SUB_VERSION: 0,
          VERSION: 2,
        },
      }
  }
}

export const constructVacanciesListParams = ({
  pageNumber = 1,
  limitNumber = 5,
  filters = [],
  searchQuery = '',
}: VacanciesListParams) => {
  const offset = (pageNumber - 1) * limitNumber
  const search = constructQueryParams({ searchQuery, filters })

  return {
    offset,
    search,
    limit: limitNumber,
    sort: {
      publicationStartDate: 'DESC',
    },
    condition: {
      type: 'AND',
      items: [
        {
          type: 'COND',
          field: 'published',
          op: 'T',
        },
        {
          type: 'COND',
          field: 'statusId',
          op: 'EQ',
          param: openVacancyStatusId,
        },
      ],
    },
    what: {
      ...vacancyParams,
    },
    getTotalCount: 1,
  }
}

export const createVacancyDetailResObj = (
  vacancyDetailRes: OtysVacancyDetailPairs | undefined,
  sid: string
): VacancyDetail | undefined => {
  if (!vacancyDetailRes) return

  const vacancyContent: VacancyContent = [
    {
      title: 'Google description',
      description: vacancyDetailRes.textField_summary?.text,
    },
    {
      title: 'Dit is je uitdaging',
      description: vacancyDetailRes.textField_description.text,
    },
    {
      title: 'Over de rol',
      description: vacancyDetailRes.textField_extra4.text,
    },
    {
      title: 'Over de organisatie',
      description: vacancyDetailRes.textField_companyProfile.text,
    },
    {
      title: 'Wat we vragen',
      description: vacancyDetailRes.textField_requirements.text,
    },
    {
      title: 'Wat we bieden',
      description: vacancyDetailRes.textField_extra1.text,
    },
    {
      title: 'Solliciteren',
      description: vacancyDetailRes.textField_extra2.text,
    },
    {
      title: 'Nog vragen?',
      description: vacancyDetailRes.textField_extra3.text,
    },
  ]

  return {
    uid: vacancyDetailRes.uid,
    title: vacancyDetailRes.title,
    location: vacancyDetailRes.location,
    salaryValue: vacancyDetailRes.salaryValue ?? null,
    hourlyRate:
      createSingleMatchCriteriaRes(vacancyDetailRes.matchCriteria_4)?.value ??
      null,
    hoursPerWeek: vacancyDetailRes.extraInfo8 ?? '',
    image: vacancyDetailRes.photoFileName,
    photoFileName: constructImageUrl({
      ouid: vacancyDetailRes.photoFileName,
      sid,
      category: ImageCategory.VACANCY,
    }),
    thumbnailImage: vacancyDetailRes.photoThumbFileName,
    shortDescription: vacancyDetailRes.textField_summary.text,
    content: vacancyContent,
    relationId: vacancyDetailRes.relationId,
    status: vacancyDetailRes.status,
    userId: vacancyDetailRes.userId,
    jobType: vacancyDetailRes.jobType || null,
    locationAddress: vacancyDetailRes.locationAddress || null,
    locationCity: vacancyDetailRes.locationCity || null,
    locationCountry: vacancyDetailRes.locationCountry || null,
    locationCountryCode: vacancyDetailRes.locationCountryCode || null,
    locationPostcode: vacancyDetailRes.locationPostcode || null,
    locationState: vacancyDetailRes.locationState || null,
    relation: vacancyDetailRes.relation || null,
    salaryUnit: vacancyDetailRes.salaryUnit || null,
    publicationStartDate: vacancyDetailRes.publicationStartDate || null,
    publicationStatusId: vacancyDetailRes.publicationStatusId || null,
  } as VacancyDetail
}

export const createPartialVacancyDetailResObj = (
  vacancyDetailRes: OtysVacancyDetailPairs | undefined
): VacancyDetail | undefined => {
  if (!vacancyDetailRes) return

  return {
    uid: vacancyDetailRes.uid ?? '',
    title: vacancyDetailRes.title ?? '',
    userId: vacancyDetailRes?.userId ?? '',
    shortDescription: vacancyDetailRes?.textField_summary?.text ?? '',
  } as VacancyDetail
}
